// Invoices — the billing ledger. A filterable list (client, contract, status)
// with a create modal that can seed the invoice with an initial set of line
// items. The status workflow (send / mark-paid / void) and the line-item
// editor live on the detail page; here we surface the lifecycle and route
// through to the detail view.
//
// Recurring invoice schedules are also managed from this page in a second
// section — they generate invoices on an RRULE cadence.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass. The detail link targets /app/invoices/:id.

import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  Contract,
  Invoice,
  InvoiceStatus,
  Organization,
  RecurringInvoice,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Select,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { ApiError } from '../api/client';
import { useOrganizations } from '../api/queries/clients';
import {
  useInvoices,
  useCreateInvoice,
  useDeleteInvoice,
  useContracts,
  useRecurringInvoices,
  useCreateRecurringInvoice,
  useUpdateRecurringInvoice,
  useDeleteRecurringInvoice,
  type InvoiceFilters,
} from '../api/queries/billing';

// ---------- Static option lists ----------

const STATUSES: { value: InvoiceStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null): string {
  return epochMs === null ? '—' : new Date(epochMs).toLocaleDateString();
}

function fmtMoney(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export function invoiceStatusTone(status: InvoiceStatus): string {
  switch (status) {
    case 'paid':
      return 'badge-success';
    case 'sent':
      return 'badge';
    case 'draft':
      return 'badge-warn';
    case 'overdue':
      return 'badge-danger';
    case 'void':
      return 'badge-muted';
    default:
      return 'badge';
  }
}

function statusLabel(status: InvoiceStatus): string {
  return STATUSES.find((s) => s.value === status)?.label ?? status;
}

// =============================================================================
// Page
// =============================================================================

export function InvoicesPage(): JSX.Element {
  const canWrite = can('invoice.write');

  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<Invoice | null>(null);

  const invoicesQuery = useInvoices(filters);
  const orgsQuery = useOrganizations();
  const contractsQuery = useContracts();
  const deleteInvoice = useDeleteInvoice();

  const invoices = invoicesQuery.data?.invoices ?? [];
  const orgs = orgsQuery.data?.organizations ?? [];
  const contracts = contractsQuery.data?.contracts ?? [];

  const orgName = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgs) map.set(o.id, o.name);
    return (id: number): string => map.get(id) ?? `Org #${id}`;
  }, [orgs]);

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteInvoice.mutateAsync(deleting.id);
      toast.success(`Deleted invoice ${deleting.invoice_no}.`);
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete invoice.'));
    }
  };

  // Filter selects use the literal "all" sentinel (Radix Select forbids an
  // empty value); "all" maps back to no filter.
  const clientFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All clients' },
    ...orgs.map((o) => ({ value: String(o.id), label: o.name })),
  ];
  const contractFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All contracts' },
    ...contracts.map((c) => ({ value: String(c.id), label: c.contract_no })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  return (
    <section className="page invoices-page">
      <header className="page-header">
        <div>
          <h2>Invoices</h2>
          <p className="page-sub">Billing ledger and recurring schedules.</p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New invoice
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={filters.organization_id !== undefined ? String(filters.organization_id) : 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              organization_id: v === 'all' ? undefined : Number(v),
            }))
          }
          options={clientFilterOptions}
          aria-label="Filter by client"
        />
        <Select
          value={filters.contract_id !== undefined ? String(filters.contract_id) : 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              contract_id: v === 'all' ? undefined : Number(v),
            }))
          }
          options={contractFilterOptions}
          aria-label="Filter by contract"
        />
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              status: v === 'all' ? undefined : (v as InvoiceStatus),
            }))
          }
          options={statusFilterOptions}
          aria-label="Filter by status"
        />
      </div>

      {invoicesQuery.isLoading && <p className="muted">Loading invoices…</p>}
      {invoicesQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(invoicesQuery.error, 'Failed to load invoices.')}
        </p>
      )}
      {!invoicesQuery.isLoading && invoices.length === 0 && (
        <p className="muted empty-state">No invoices match these filters.</p>
      )}

      {invoices.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Invoice #</Table.HeaderCell>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Issued</Table.HeaderCell>
              <Table.HeaderCell>Due</Table.HeaderCell>
              <Table.HeaderCell>Subtotal</Table.HeaderCell>
              <Table.HeaderCell>Tax</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {invoices.map((inv) => (
              <Table.Row key={inv.id}>
                <Table.Cell tone="strong">
                  <Link to={`/app/invoices/${inv.id}`} className="link">
                    {inv.invoice_no}
                  </Link>
                </Table.Cell>
                <Table.Cell>{orgName(inv.organization_id)}</Table.Cell>
                <Table.Cell>
                  <Badge className={invoiceStatusTone(inv.status)}>
                    {statusLabel(inv.status)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{fmtDate(inv.issue_date)}</Table.Cell>
                <Table.Cell>{fmtDate(inv.due_date)}</Table.Cell>
                <Table.Cell>{fmtMoney(inv.subtotal)}</Table.Cell>
                <Table.Cell>{fmtMoney(inv.tax_amount)}</Table.Cell>
                <Table.Cell>{fmtMoney(inv.total)}</Table.Cell>
                <Table.Cell className="col-actions">
                  <Button asChild variant="ghost" className="btn-sm">
                    <Link to={`/app/invoices/${inv.id}`}>Open</Link>
                  </Button>
                  {canWrite && (
                    <Button variant="danger" className="btn-sm" onClick={() => setDeleting(inv)}>
                      Delete
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <RecurringInvoicesSection
        orgs={orgs}
        contracts={contracts}
        orgName={orgName}
        canWrite={canWrite}
      />

      {/* Create */}
      {createOpen && (
        <InvoiceFormModal
          orgs={orgs}
          contracts={contracts}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {/* Delete */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete invoice"
        message={
          deleting
            ? `Delete invoice ${deleting.invoice_no}? Consider voiding instead to preserve the billing trail.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteInvoice.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}

// =============================================================================
// Create invoice modal (with optional seed line items)
// =============================================================================

interface SeedLine {
  description: string;
  quantity: string;
  unit_amount: string;
}

function lineTotal(qty: string, unit: string): number {
  const q = Number(qty);
  const u = Number(unit);
  if (!Number.isFinite(q) || !Number.isFinite(u)) return 0;
  return Math.round(q * u * 100) / 100;
}

interface InvoiceFormModalProps {
  orgs: Organization[];
  contracts: Contract[];
  onClose: () => void;
}

function InvoiceFormModal({ orgs, contracts, onClose }: InvoiceFormModalProps): JSX.Element {
  const create = useCreateInvoice();
  const busy = create.isPending;

  const [organizationId, setOrganizationId] = useState<number | ''>('');
  const [contractId, setContractId] = useState<number | ''>('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [issueDate, setIssueDate] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<number | null>(null);
  const [taxAmount, setTaxAmount] = useState('0');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<SeedLine[]>([]);

  // Contracts are scoped to the selected client.
  const contractsForOrg = useMemo(
    () =>
      organizationId === ''
        ? []
        : contracts.filter((c) => c.organization_id === organizationId),
    [contracts, organizationId],
  );

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + lineTotal(l.quantity, l.unit_amount), 0),
    [lines],
  );
  const taxNum = Number(taxAmount);
  const grandTotal = subtotal + (Number.isFinite(taxNum) ? taxNum : 0);

  const addLine = (): void =>
    setLines((ls) => [...ls, { description: '', quantity: '1', unit_amount: '0' }]);

  const updateLine = (idx: number, patch: Partial<SeedLine>): void =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const removeLine = (idx: number): void =>
    setLines((ls) => ls.filter((_, i) => i !== idx));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (organizationId === '') {
      toast.warn('Select a client.');
      return;
    }
    const trimmedNo = invoiceNo.trim();
    if (!trimmedNo) {
      toast.warn('Invoice number is required.');
      return;
    }
    const tax = taxAmount.trim() || '0';
    if (!/^\d+(\.\d{1,2})?$/.test(tax)) {
      toast.warn('Tax must be a non-negative amount.');
      return;
    }
    // Validate seed line items.
    for (const l of lines) {
      if (!/^\d+(\.\d{1,2})?$/.test(l.quantity.trim())) {
        toast.warn('Each line quantity must be a non-negative number.');
        return;
      }
      if (!/^\d+(\.\d{1,2})?$/.test(l.unit_amount.trim())) {
        toast.warn('Each line unit amount must be a non-negative number.');
        return;
      }
    }
    const trimmedNotes = notes.trim();

    try {
      await create.mutateAsync({
        organization_id: organizationId,
        invoice_no: trimmedNo,
        contract_id: contractId === '' ? null : contractId,
        // subtotal is the sum of the seed lines (the DB recomputes line_total
        // per row; the invoice subtotal/total are maintained by the caller).
        subtotal: subtotal.toFixed(2),
        tax_amount: tax,
        ...(issueDate !== null ? { issue_date: issueDate } : {}),
        ...(dueDate !== null ? { due_date: dueDate } : {}),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        ...(lines.length > 0
          ? {
              line_items: lines.map((l) => ({
                source_type: 'manual' as const,
                description: l.description.trim() || undefined,
                quantity: l.quantity.trim(),
                unit_amount: l.unit_amount.trim(),
              })),
            }
          : {}),
      });
      toast.success(`Created invoice ${trimmedNo}.`);
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to create invoice.'));
    }
  };

  return (
    <Dialog
      title="New invoice"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="invoice-form" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="invoice-form" onSubmit={onSubmit} className="form-grid">
        <div className="form-row">
          <FormField label="Client">
            <select
              className="form-input"
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value ? Number(e.target.value) : '');
                setContractId('');
              }}
              required
            >
              <option value="">Select a client…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Contract">
            <select
              className="form-input"
              value={contractId}
              onChange={(e) => setContractId(e.target.value ? Number(e.target.value) : '')}
              disabled={organizationId === ''}
            >
              <option value="">None</option>
              {contractsForOrg.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_no}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Invoice number">
          <Input
            type="text"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            maxLength={120}
            required
          />
        </FormField>

        <div className="form-row">
          <FormField label="Issue date">
            <DateInput value={issueDate} onChange={setIssueDate} />
          </FormField>
          <FormField label="Due date">
            <DateInput value={dueDate} onChange={setDueDate} />
          </FormField>
        </div>

        {/* Seed line items */}
        <div className="form-field">
          <div className="form-subhead">
            <span>Line items</span>
            <Button variant="ghost" className="btn-sm" onClick={addLine}>
              Add line
            </Button>
          </div>
          {lines.length === 0 ? (
            <p className="muted form-hint">
              Optional. You can also add line items after creating the invoice.
            </p>
          ) : (
            <Table className="compact">
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Qty</Table.HeaderCell>
                  <Table.HeaderCell>Unit</Table.HeaderCell>
                  <Table.HeaderCell>Total</Table.HeaderCell>
                  <Table.HeaderCell aria-label="Remove" />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {lines.map((l, i) => (
                  <Table.Row key={i}>
                    <Table.Cell>
                      <Input
                        type="text"
                        value={l.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                        placeholder="Service description"
                        maxLength={2000}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        type="text"
                        inputMode="decimal"
                        className="narrow"
                        value={l.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        type="text"
                        inputMode="decimal"
                        className="narrow"
                        value={l.unit_amount}
                        onChange={(e) => updateLine(i, { unit_amount: e.target.value })}
                      />
                    </Table.Cell>
                    <Table.Cell>{fmtMoney(lineTotal(l.quantity, l.unit_amount).toFixed(2))}</Table.Cell>
                    <Table.Cell>
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => removeLine(i)}
                        aria-label="Remove line"
                      >
                        ×
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>

        <div className="form-row">
          <FormField label="Subtotal (computed)">
            <Input
              type="text"
              value={fmtMoney(subtotal.toFixed(2))}
              readOnly
              disabled
            />
          </FormField>
          <FormField label="Tax (USD)">
            <Input
              type="text"
              inputMode="decimal"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
              placeholder="0.00"
            />
          </FormField>
        </div>

        <div className="totals-summary">
          <strong>Total: {fmtMoney(grandTotal.toFixed(2))}</strong>
        </div>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={20_000}
            placeholder="Optional"
          />
        </FormField>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Recurring invoices section
// =============================================================================

interface RecurringSectionProps {
  orgs: Organization[];
  contracts: Contract[];
  orgName: (id: number) => string;
  canWrite: boolean;
}

function RecurringInvoicesSection({
  orgs,
  contracts,
  orgName,
  canWrite,
}: RecurringSectionProps): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RecurringInvoice | null>(null);
  const [deleting, setDeleting] = useState<RecurringInvoice | null>(null);

  const recurringQuery = useRecurringInvoices();
  const deleteRecurring = useDeleteRecurringInvoice();

  const schedules = recurringQuery.data?.recurring_invoices ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteRecurring.mutateAsync(deleting.id);
      toast.success('Deleted recurring schedule.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete schedule.'));
    }
  };

  return (
    <section className="page-section">
      <div className="section-head">
        <h3>Recurring schedules</h3>
        {canWrite && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setCreateOpen(true)}
          >
            New schedule
          </button>
        )}
      </div>

      {recurringQuery.isLoading && <p className="muted">Loading schedules…</p>}
      {recurringQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(recurringQuery.error, 'Failed to load schedules.')}
        </p>
      )}
      {!recurringQuery.isLoading && schedules.length === 0 && (
        <p className="muted empty-state">No recurring schedules configured.</p>
      )}

      {schedules.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>RRULE</Table.HeaderCell>
              <Table.HeaderCell>Next due</Table.HeaderCell>
              <Table.HeaderCell>Active</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {schedules.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell>{orgName(r.organization_id)}</Table.Cell>
                <Table.Cell className="cell-truncate code">{r.rrule}</Table.Cell>
                <Table.Cell>{fmtDate(r.next_due_at)}</Table.Cell>
                <Table.Cell>
                  {r.active ? (
                    <Badge className="badge-success">Active</Badge>
                  ) : (
                    <Badge className="badge-muted">Paused</Badge>
                  )}
                </Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button variant="ghost" className="btn-sm" onClick={() => setEditing(r)}>
                      Edit
                    </Button>
                    <Button variant="danger" className="btn-sm" onClick={() => setDeleting(r)}>
                      Delete
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <RecurringFormModal
          mode="create"
          orgs={orgs}
          contracts={contracts}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {editing && (
        <RecurringFormModal
          mode="edit"
          recurring={editing}
          orgs={orgs}
          contracts={contracts}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete recurring schedule"
        message="Delete this recurring invoice schedule? No more invoices will be generated from it."
        confirmLabel="Delete"
        danger
        busy={deleteRecurring.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}

interface RecurringFormModalProps {
  mode: 'create' | 'edit';
  recurring?: RecurringInvoice;
  orgs: Organization[];
  contracts: Contract[];
  onClose: () => void;
}

function RecurringFormModal({
  mode,
  recurring,
  orgs,
  contracts,
  onClose,
}: RecurringFormModalProps): JSX.Element {
  const create = useCreateRecurringInvoice();
  const update = useUpdateRecurringInvoice();
  const busy = create.isPending || update.isPending;

  const [organizationId, setOrganizationId] = useState<number | ''>(
    recurring?.organization_id ?? '',
  );
  const [contractId, setContractId] = useState<number | ''>(
    recurring?.contract_id ?? '',
  );
  const [rrule, setRrule] = useState(recurring?.rrule ?? 'FREQ=MONTHLY;INTERVAL=1');
  const [nextDueAt, setNextDueAt] = useState<number | null>(
    recurring?.next_due_at ?? null,
  );
  const [active, setActive] = useState(recurring?.active ?? true);
  const [templateText, setTemplateText] = useState(() =>
    recurring ? JSON.stringify(recurring.template, null, 2) : '{}',
  );
  const [templateError, setTemplateError] = useState<string | null>(null);

  const contractsForOrg = useMemo(
    () =>
      organizationId === ''
        ? []
        : contracts.filter((c) => c.organization_id === organizationId),
    [contracts, organizationId],
  );

  const parseTemplate = (): Record<string, unknown> | undefined => {
    const text = templateText.trim();
    if (text === '') return {};
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setTemplateError('Template must be a JSON object.');
        return undefined;
      }
      setTemplateError(null);
      return parsed as Record<string, unknown>;
    } catch {
      setTemplateError('Template is not valid JSON.');
      return undefined;
    }
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const template = parseTemplate();
    if (template === undefined) return;
    const trimmedRrule = rrule.trim();
    if (!trimmedRrule) {
      toast.warn('An RRULE is required.');
      return;
    }

    try {
      if (mode === 'create') {
        if (organizationId === '') {
          toast.warn('Select a client.');
          return;
        }
        await create.mutateAsync({
          organization_id: organizationId,
          contract_id: contractId === '' ? null : contractId,
          rrule: trimmedRrule,
          active,
          template,
          ...(nextDueAt !== null ? { next_due_at: nextDueAt } : {}),
        });
        toast.success('Created recurring schedule.');
      } else if (recurring) {
        await update.mutateAsync({
          id: recurring.id,
          contract_id: contractId === '' ? null : contractId,
          rrule: trimmedRrule,
          active,
          template,
          next_due_at: nextDueAt,
        });
        toast.success('Recurring schedule updated.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save schedule.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New recurring schedule' : 'Edit recurring schedule'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="recurring-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="recurring-form" onSubmit={onSubmit} className="form-grid">
        <div className="form-row">
          <FormField label="Client">
            <select
              className="form-input"
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value ? Number(e.target.value) : '');
                setContractId('');
              }}
              disabled={mode === 'edit'}
              required={mode === 'create'}
            >
              <option value="">Select a client…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Contract">
            <select
              className="form-input"
              value={contractId}
              onChange={(e) => setContractId(e.target.value ? Number(e.target.value) : '')}
              disabled={organizationId === ''}
            >
              <option value="">None</option>
              {contractsForOrg.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_no}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="RRULE">
          <Input
            type="text"
            className="code-input"
            value={rrule}
            onChange={(e) => setRrule(e.target.value)}
            maxLength={2000}
            placeholder="FREQ=MONTHLY;INTERVAL=1"
            required
          />
          <span className="muted form-hint">
            iCalendar recurrence rule that drives the generation cadence.
          </span>
        </FormField>

        <div className="form-row">
          <FormField label="Next due">
            <DateInput value={nextDueAt} onChange={setNextDueAt} withTime />
          </FormField>
          <label className="form-field checkbox-field">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <span>Active</span>
          </label>
        </div>

        <FormField label="Invoice template (JSON)">
          <Textarea
            className={`code-input ${templateError ? 'invalid' : ''}`}
            value={templateText}
            onChange={(e) => setTemplateText(e.target.value)}
            rows={8}
            spellCheck={false}
            placeholder="{}"
          />
          {templateError && (
            <span className="field-error" role="alert">
              {templateError}
            </span>
          )}
          <span className="muted form-hint">
            Blueprint for the line items each generated invoice receives.
          </span>
        </FormField>
      </form>
    </Dialog>
  );
}
